import { Component, OnInit } from '@angular/core';
import { StudentCurriculumService } from '../services/studentcurriculum.service';
import { StudentCurriculumModel, CourseModel, ModuleStatus, CourseStatus, ContractStatus, PhaseModel, ModuleModel, ContractType, EducationDurationUnits, CourseVariant } from '../models/student.curriculum.model';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { MatIconRegistry } from '@angular/material/icon';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { BaseResizeComponent } from 'loi-lti-common';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { formatDate } from '@angular/common';
import { LtiRequestService } from '../services/lti-request.service';
import '../extensions/string.extensions';
import { RoleService } from '../services/role.service';
import { MembershipService } from '../services/membership.service';
import { CampusUrlService } from '../services/campus-url.service';

export interface ModuleCodeName {
  vioCode: string;
  name: string;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})

export class HomeComponent extends BaseResizeComponent implements OnInit {
  public studentCurriculum: StudentCurriculumModel;
  public loading: boolean = true;
  public contextCourse: CourseModel;
  public contextCoursePhases: PhaseModel[] = [];
  filters: string[] = [];
  searchFilter: string = "";
  iconClassesInterval: any;
  moduleNames: ModuleCodeName[] = [];
  isStudent: boolean = true;
  isMultiPhaseCourse: boolean = true;
  CourseVariant = CourseVariant;

  constructor(
    private studentCurriculumService: StudentCurriculumService,
    private translateService: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private membershipService: MembershipService,
    private ltiRequestService: LtiRequestService,
    private roleService: RoleService,
    private campusUrlService: CampusUrlService) {
    super();
    this.matIconRegistry.addSvgIcon('expand_more', this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/expand_more.svg'));
    this.matIconRegistry.addSvgIcon('search', this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/search.svg'));
    this.matIconRegistry.addSvgIcon('info', this.domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons/information.svg'));
  }

  ngOnInit() {
    super.resize();
    let reload = this.route.snapshot.paramMap.get('reload') != 'false';
    if (reload) {

      this.ltiRequestService.getLtiRequest().subscribe(ltiResponseModel => {
        let ltiRequest = ltiResponseModel.ltiRequest;

        this.roleService.getRole().subscribe(role => {
          role = role.toUpperCase();
          sessionStorage.setItem('role', JSON.stringify(role));
          this.isStudent = ['LEARNER', 'MEMBER', 'NONCREDITLEARNER'].indexOf(role) != -1;

          //About the module
          if (ltiRequest.resource_link_title != null && ltiRequest.resource_link_title.toUpperCase() == 'OVER DE MODULE') {
            this.router.navigate(['/module_details/:studentEid/:moduleId/:aboutTheModule', {
              studentEid: ltiRequest.lis_person_sourcedid,
              moduleId: ltiRequest.lis_course_offering_sourcedid,
              aboutTheModule: true
            }]);
          }

          //Module overview
          else {
            if (this.isStudent) {
              console.log('calling studentcurriculum service');
              this.studentCurriculumService.getStudentCurriculum().subscribe(studentCurriculum => {
                console.log(`retreived ${studentCurriculum.courses.length} opleidingen, contextInternalCourseCode: ${studentCurriculum.contextInternalCourseCode}`);
                this.studentCurriculum = studentCurriculum;
                this.proceedWithInit();
              }, error => console.error(error));
            }
            else { //we show a tekst saying that the module overview is only for students
              super.ngOnInit();
            }
          }
        }, error => console.error(error));
      }, error => console.error(error));
    }

    //Returning from a detail page, reload data from session (only occurs for Module overview)
    else {
      this.studentCurriculum = JSON.parse(sessionStorage.getItem('studentCurriculum'));
      this.proceedWithInit();
    }
  }

  async proceedWithInit() {
    let contextCourseCode = this.studentCurriculum.contextInternalCourseCode;
    if (contextCourseCode.endsWith('a') || contextCourseCode.endsWith('k')) {
      contextCourseCode = contextCourseCode.substring(0, contextCourseCode.length - 1);
    }
    for (let course of this.studentCurriculum.courses) {
      console.log(`processing course, name ${course.name}, internal course code ${course.internalCode}, external course code ${course.externalCode}`);
      //take the course with one of the preferred statusses if more than one course with the coursecode is available
      let preferredStatuses = [ContractStatus.INGESCHREVEN, ContractStatus.INGESCHREVENNIETTOELAATBAAR, ContractStatus.GEDIPLOMEERD, ContractStatus.AFSTUDEREN];
      if (course.internalCode == contextCourseCode) {
        if (this.contextCourse == null ||
          (
            preferredStatuses.indexOf(this.contextCourse.serviceContract.contractStatus) == -1 &&
            preferredStatuses.indexOf(course.serviceContract.contractStatus) > -1
          )) {
          this.contextCourse = course;
          if (course.educationDurationUnit == EducationDurationUnits.MONTHS && course.educationDuration != null && course.educationDuration <= 24) {
            this.isMultiPhaseCourse = false;
          }
          this.copyModulesToPhases(course);
          course.infoText = await this.getCourseInfoText(course);
        }
      }
    }
    this.loading = false;
    super.ngOnInit();
  }

  copyModulesToPhases(course: CourseModel) {
    this.moduleNames = [];
    this.contextCoursePhases = [];
    for (let module of course.modules) {
      this.moduleNames.push({ vioCode: module.internalCode, name: module.name });
      if (module.phase == null || this.isMultiPhaseCourse == false) {
        module.phase = "";
      }
      let phase = this.contextCoursePhases.find(p => p.name.toLowerCase() == module.phase.toLowerCase());
      if (module.status === ModuleStatus.ALWAYS_ACTIVE) {
        phase = this.contextCoursePhases.find(p => p.name.toLowerCase() === 'altijd actieve modules');
      }
      if (phase == null || phase === undefined) {
        if (module.status === ModuleStatus.ALWAYS_ACTIVE) {
          phase = new PhaseModel('Altijd actieve modules', 0);
        } else {
          phase = new PhaseModel(module.phase.charAt(0).toUpperCase() + module.phase.substr(1).toLowerCase(), module.phase.toLowerCase() == "propedeuse" ? 1 : 2);
        }
        this.contextCoursePhases.push(phase);
        console.log(`added phase: ${JSON.stringify(phase)}`);
      }
      phase.modules.push(module);
      this.getModuleStatusText(module).subscribe(statusText => {
        module.statusText = statusText;
      }, error => console.error(`failed to load text for moduleStatus ${ModuleStatus[module.status]}, error: ${error}`));
    }
    this.contextCoursePhases.sort((p1, p2) => p1.order - p2.order);
  }

  getModuleIconClass(module: ModuleModel) {
    switch (module.status) {
      case ModuleStatus.AVAILABLE: {
        return "module-status-icon available-module-status-icon";
      }
      case ModuleStatus.ACTIVATED_FROM_START: {
        return "module-status-icon activated-status-icon";
      }
      case ModuleStatus.ACTIVATED: {
        if (module.activationInProgress == true) {
          return "module-status-icon inprogress-status-icon";
        }
        else {
          return "module-status-icon activated-status-icon";
        }
      }
      case ModuleStatus.ALWAYS_ACTIVE: {
        return "module-status-icon activated-status-icon";
      }
      case ModuleStatus.FINISHED: {
        return "module-status-icon finished-status-icon";
      }
      case ModuleStatus.EXEMPTION: {
        return "module-status-icon exemption-status-icon";
      }
      case ModuleStatus.UNAVAILABLE: {
        return "module-status-icon unavailable-status-icon";
      }
      default: {
        return "module-status-icon";
      }
    }
  }

  getEarnedCredits(course: CourseModel): number {
    let earnedCredits: number = 0;
    for (let module of course.modules) {
      if ([ModuleStatus.FINISHED, ModuleStatus.EXEMPTION].indexOf(module.status) > -1) {
        earnedCredits += module.defaultCredits;
      }
    }
    return earnedCredits;
  }

  getEarnedCreditsProgress(course: CourseModel): number {
    if (course != null && course.defaultCredits > 0) {
      return (this.getEarnedCredits(course) / course.defaultCredits) * 100;
    }
    else {
      return 0;
    }
  }

  getFinishedModuleCount(course: CourseModel): number {
    let count: number = 0;
    for (let module of course.modules) {
      if ([ModuleStatus.FINISHED, ModuleStatus.EXEMPTION].indexOf(module.status) > -1) {
        count++;
      }
    }
    return count;
  }

  async getCourseInfoText(course: CourseModel) {
    let courseStatus = course.courseStatus;
    if (courseStatus == CourseStatus.NOT_ADMITTED_AND_STARTED) {
      if (course.serviceContract.contractType == ContractType.AFSTAND) {
        courseStatus = CourseStatus.FOLLOW_UP_AND_STARTED_AND_STUDENT_ACTIVATED;
      }
      else {
        courseStatus = CourseStatus.FOLLOW_UP_AND_STARTED_AND_LOI_ACTIVATED;
      }
    }
    let selfServiceUrl = await this.campusUrlService.getSelfServiceUrl().toPromise();
    return await this.translateService.get(`CourseStatusInfoText_${CourseStatus[courseStatus]}`, {
      startDate: this.loiFormattedDate(course.serviceContract.startDate),
      activationMax: course.activationMax,
      endDate: this.loiFormattedDate(course.serviceContract.endDate),
      renewalsRemaining: course.serviceContract.renewalsRemaining,
      contractStatus: ContractStatus[course.serviceContract.contractStatus],
      selfServiceLink: selfServiceUrl
    }).toPromise();
  }

  goToModuleDetails(studentEid, module: ModuleModel) {
    const moduleId = module.internalCode;
    const activeModuleStatus = [ModuleStatus.ACTIVATED_FROM_START, ModuleStatus.ALWAYS_ACTIVE, ModuleStatus.FINISHED];
    if (module.status == ModuleStatus.ACTIVATED) {
      // check if the activated module has membership
      this.membershipService.hasMembership(moduleId).subscribe(hasMembership => {
        this.navigateToModule(studentEid, module, hasMembership);
      })
    } else {
      this.navigateToModule(studentEid, module, activeModuleStatus.includes(module.status));
    }
  }

  navigateToModule(studentEid, module: ModuleModel, isActiveNodule: boolean) {
    const moduleId = module.internalCode;
    if (!isActiveNodule) {
      sessionStorage.setItem('studentCurriculum', JSON.stringify(this.studentCurriculum));
      sessionStorage.setItem('module', JSON.stringify(module));
      sessionStorage.setItem('serviceContract', JSON.stringify(this.contextCourse.serviceContract));
      sessionStorage.setItem('moduleNames', JSON.stringify(this.moduleNames));
      this.router.navigate(['/module_details/:studentEid/:moduleId', { studentEid, moduleId }]);
    } else {
      this.navigateToModuleLearningPath(moduleId);
    }
  }

  getModuleStatusText(module: ModuleModel): Observable<string> {
    if (module.activationInProgress == true) {
      return this.translateService.get(`Module_Status_INPROGRESS`, {
        activeUntil: this.loiFormattedDate(module.activationEndDate)
      });
    }
    else {
      return this.translateService.get(`Module_Status_${ModuleStatus[module.status]}`, {
        activeUntil: this.loiFormattedDate(module.activationEndDate)
      });
    }
  }

  isExemptionOrUnavailable(module: ModuleModel) {
    return [ModuleStatus.EXEMPTION, ModuleStatus.UNAVAILABLE].indexOf(module.status) > -1;
  }

  loiFormattedDate(date: string): string {
    return formatDate(date, 'dd-MM-yyyy', this.translateService.defaultLang);
  }

  checkboxChanged(e: MatCheckboxChange, checkBoxStatusName: string) {
    console.log(`checkbox changed, checked: ${e.checked}, checkboxName: ${checkBoxStatusName}`);
    if (e.checked) {
      this.filters.push(checkBoxStatusName);
    }
    else {
      this.filters.splice(this.filters.indexOf(checkBoxStatusName), 1);
    }
  }

  isModuleInFilter(status: number, name: string): boolean {
    if (status == ModuleStatus.EXEMPTION) {
      status = ModuleStatus.FINISHED;
    }
    return ((this.filters.length == 0) || (this.filters.indexOf(ModuleStatus[status].toLowerCase()) != -1)) &&
      (this.searchFilter == "" || name.toLowerCase().indexOf(this.searchFilter.toLowerCase()) != -1);
  }

  getFilterCount(filterNames: string): number {
    let count = 0;
    for (let filter of filterNames.split(',')) {
      count += this.contextCourse.modules.filter(module => ModuleStatus[module.status].toLowerCase() == filter.trim()).length;
    }
    return count;
  }

  getDefaultCreditsUnit(): string {
    let unit = this.contextCourse.defaultCreditsUnit;
    if (unit == 'Maanden') {
      unit = unit.toLowerCase();
    } else {
      unit = unit + "'s";
    }
    return unit;
  }

  navigateToModuleLearningPath(moduleId: string) : void {
    const hosts = document.referrer.match(/http[s]?\:\/\/[^\/]+/g);
    const host = hosts && hosts[0];
    this.campusUrlService.getLearningPathUrlForModule(moduleId).subscribe(url => {
      window.top.location.href = url;
    });
  }

}
